import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { getDocumentIdToQueueItemMap, getRejectionsByDocumentId } from '@/helpers/queues';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetPerformProgressQuery } from '@/services/organizations';
import { Accordion, Alert, Badge, Box, Button, Card, Modal, useOpenClose } from '@palmetto/palmetto-components';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { DocumentDocument, ProgramType, UnderwritingDocumentTypes } from 'types';

import { useUploadDocumentMutation } from '../../services/documents';
import { useGetQueuesByAccountIdAndDocumentTypeQuery } from '../../services/queues';
import { AccountRequirementStatus } from '../../types/Milestone';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { StatusIcon } from '../StatusIcon';

import { DocumentItem } from './DocumentItem';

interface IdentityVerificationProps {
  status: AccountRequirementStatus;
  creditStatus: AccountRequirementStatus;
  programType: ProgramType;
}

export function IdentityVerification({ status, creditStatus, programType }: IdentityVerificationProps) {
  const isApproved = status === AccountRequirementStatus.completed;
  const { isOpen: isSectionOpen, handleToggle: handleToggleSection } = useOpenClose({ defaultIsOpen: !isApproved });
  const { isOpen, handleClose, handleOpen: openUploadModal } = useOpenClose();
  const [files, setFiles] = useState<FileList | undefined>();
  const { id } = useParams<{ id: any }>();
  const allowsAction = useIsOrgReadOnly();

  const { data: documents, isLoading: isDocumentsLoading } = useGetDocumentsQuery({
    accountId: id,
    documentTypeFilter: [UnderwritingDocumentTypes.identityVerification],
  });
  const { data: queueItems, isLoading: isGetQueueByAccountIdLoading } = useGetQueuesByAccountIdAndDocumentTypeQuery(
    {
      accountId: id,
      type: UnderwritingDocumentTypes.identityVerification,
    },
    { skip: isApproved && !isSectionOpen },
  );
  const isLoading = isDocumentsLoading || isGetQueueByAccountIdLoading;
  const [uploadDocument] = useUploadDocumentMutation();
  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);
  const documentIdToQueueItemMap = useMemo(() => getDocumentIdToQueueItemMap(queueItems), [queueItems]);
  const rejectionsByDocumentId = useMemo(() => getRejectionsByDocumentId(queueItems), [queueItems]);

  const onFileChange = (event: any) => {
    setFiles(event.target?.files);
  };

  const closeUploadModal = () => {
    setFiles(undefined);
    handleClose();
  };

  const handleFileUpload = () => {
    if (!files || !files.length) return;

    const data = new FormData();
    data.append('grouped', 'true');
    data.append('type', UnderwritingDocumentTypes.identityVerification);

    for (let i = 0; i < files.length; i++) {
      data.append(`files[${i}]`, files[i]);
    }

    closeUploadModal();

    uploadDocumentsWithToast({
      uploadDocument,
      accountId: id,
      formData: data,
      invalidateAllTags: true,
      files,
      messages: {
        success: () => 'Identity verification uploaded',
      },
    });
  };

  const creditApplicationRequired = [
    ProgramType.solar,
    ProgramType.hvac,
    ProgramType.waterHeater,
    ProgramType.generator,
    ProgramType.energyBackup,
  ].includes(programType);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onDismiss={closeUploadModal}
        maxWidth="4xl"
        ariaLabelledBy="uploadIdentityVerificationHeader"
      >
        <Modal.Header
          id="uploadIdentityVerificationHeader"
          title="Upload Proof of Identity"
          onDismiss={closeUploadModal}
        />
        <Modal.Body background="secondary" childGap="md">
          <Alert
            hasIcon
            variant="info"
            message="Verification is faster if the customer submits proof of identity via their app account"
          />
          <Box>Acceptable docs:</Box>
          <Box as="ul" childGap="xs">
            <li>Drivers license or other state issued ID card</li>
            <li>Passport or passport card</li>
            <li>Resident permit ID or green card</li>
          </Box>
          <ResettingFileUpload
            buttonText="Choose File(s)"
            name="files"
            labelText="Choose File(s)"
            id="files"
            variant="secondary"
            tone="neutral"
            multiple
            size="md"
            required
            requiredIndicator=""
            onChange={onFileChange}
            files={files}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={closeUploadModal}>
            Cancel
          </Button>
          <Button onClick={handleFileUpload} isDisabled={!files || !files.length}>
            Upload
          </Button>
        </Modal.Footer>
      </Modal>
      <Card.Section>
        {isApproved ? (
          <Accordion.Panel isOpen={isSectionOpen} borderWidth="0">
            <Accordion.PanelSummary
              isDetailsOpen={isSectionOpen}
              onToggle={handleToggleSection}
              padding="0"
              hasCaret="right"
            >
              <Box childGap="lg" alignItems="center" direction="row" justifyContent="space-between" flex="auto">
                <Box direction="row" alignItems="center">
                  <Box padding="0 xs 0 0">
                    <StatusIcon status={status} size="md" />
                  </Box>
                  <Box as="h3" fontWeight="medium" fontSize="md">
                    Identity Verification
                  </Box>
                </Box>
                <Badge message="APPROVED" variant="success" />
              </Box>
            </Accordion.PanelSummary>
            <Accordion.PanelDetails padding="0">
              {documents?.length && !isLoading ? (
                <Box childGap="lg" display="block" padding="md 0 0 0">
                  {(documents ?? []).map((doc: DocumentDocument) => {
                    const key = doc.id?.toString?.() ?? '';
                    const queueItem = documentIdToQueueItemMap[key];
                    const rejections = rejectionsByDocumentId.get(key) ?? [];
                    return (
                      <DocumentItem
                        key={key}
                        item={doc}
                        queue={queueItem}
                        showDocumentActions
                        rejections={rejections}
                      />
                    );
                  })}
                </Box>
              ) : null}
            </Accordion.PanelDetails>
          </Accordion.Panel>
        ) : (
          <>
            <Box childGap="lg" alignItems="center" direction="row" justifyContent="space-between">
              <Box direction="row" alignItems="center">
                <Box padding="0 xs 0 0">
                  <StatusIcon status={status} size="md" />
                </Box>
                <Box as="h3" fontWeight="medium" fontSize="md">
                  Identity Verification
                </Box>
              </Box>
              {creditApplicationRequired && creditStatus !== 'completed' ? (
                <Box fontWeight="medium" fontSize="sm" color="body-secondary">
                  Pending Credit Application
                </Box>
              ) : (
                <Box>
                  <Button
                    size="sm"
                    disabled={!canPerformProgressBasedOnOrgStatus || !allowsAction}
                    style={{ width: 'fit-content' }}
                    onClick={openUploadModal}
                    variant="secondary"
                    tone="neutral"
                  >
                    Upload Proof of Identity
                  </Button>
                </Box>
              )}
            </Box>
            {documents?.length && !isLoading ? (
              <Box childGap="lg" display="block" padding="sm 0 0 0">
                {(documents ?? []).map((doc: DocumentDocument) => {
                  const key = doc.id?.toString?.() ?? '';
                  const queueItem = documentIdToQueueItemMap[key];
                  const rejections = rejectionsByDocumentId.get(key) ?? [];
                  return (
                    <DocumentItem
                      key={key}
                      item={doc}
                      queue={queueItem}
                      showDocumentActions
                      rejections={rejections}
                    />
                  );
                })}
              </Box>
            ) : null}
          </>
        )}
      </Card.Section>
    </>
  );
}
